import React, { useState } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Reporting() {
  const [activeTab, setActiveTab] = useState("completion");
  const [search, setSearch] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState("");

  // --- COURSE COMPLETION MOCK DATA ---
  const mockData = [
    { user: "Alice", group: "Group A", course: "Safety Training", status: "Completed" },
    { user: "Bob", group: "Group A", course: "Safety Training", status: "In Progress" },
    { user: "Charlie", group: "Group B", course: "Customer Service", status: "Not Started" },
    { user: "Dana", group: "Group C", course: "Safety Training", status: "Overdue" },
    { user: "Eli", group: "Group B", course: "Customer Service", status: "Completed" },
  ];

  const filteredData = mockData.filter(
    (row) =>
      row.user.toLowerCase().includes(search.toLowerCase()) ||
      row.course.toLowerCase().includes(search.toLowerCase()) ||
      row.group.toLowerCase().includes(search.toLowerCase())
  );

  const statusCounts = filteredData.reduce((acc, curr) => {
    acc[curr.status] = (acc[curr.status] || 0) + 1;
    return acc;
  }, {});

  const chartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: ["#22c55e", "#facc15", "#3b82f6", "#ef4444"],
        borderColor: "#fff",
      },
    ],
  };

  const exportCourseCSV = () => {
    const csv = [
      ["User", "Group", "Course", "Status"],
      ...filteredData.map((row) => [row.user, row.group, row.course, row.status]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "course_completion_report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // --- SURVEY RESULTS MOCK DATA ---
  const surveyList = ["Leadership Feedback", "Onboarding Feedback"];
  const responses = {
    "Leadership Feedback": [
      {
        question: "The manager communicates clearly",
        results: { "Strongly Agree": 4, Agree: 3, Neutral: 1, Disagree: 1, "Strongly Disagree": 1 },
      },
      {
        question: "The manager supports development",
        results: { "Strongly Agree": 3, Agree: 5, Neutral: 1, Disagree: 1, "Strongly Disagree": 0 },
      },
    ],
    "Onboarding Feedback": [
      {
        question: "The training was helpful",
        results: { "Strongly Agree": 6, Agree: 2, Neutral: 1, Disagree: 0, "Strongly Disagree": 1 },
      },
      {
        question: "I felt welcome on my first day",
        results: { "Strongly Agree": 7, Agree: 2, Neutral: 1, Disagree: 0, "Strongly Disagree": 0 },
      },
    ],
  };

  const exportSurveyCSV = () => {
    if (!selectedSurvey) return;

    const csv = [
      ["Question", "Strongly Agree", "Agree", "Neutral", "Disagree", "Strongly Disagree"],
      ...responses[selectedSurvey].map((q) => [
        q.question,
        q.results["Strongly Agree"] ?? 0,
        q.results["Agree"] ?? 0,
        q.results["Neutral"] ?? 0,
        q.results["Disagree"] ?? 0,
        q.results["Strongly Disagree"] ?? 0,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "survey_report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-10">
      <h2 className="text-2xl font-bold mb-4">Reporting</h2>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab("completion")}
          className={`px-4 py-2 rounded ${
            activeTab === "completion" ? "bg-black text-white" : "bg-gray-200"
          }`}
        >
          Course Completion
        </button>
        <button
          onClick={() => setActiveTab("survey")}
          className={`px-4 py-2 rounded ${
            activeTab === "survey" ? "bg-black text-white" : "bg-gray-200"
          }`}
        >
          Survey Results
        </button>
      </div>

      {activeTab === "completion" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <input
              type="text"
              placeholder="Search by user, group, or course"
              className="border rounded px-4 py-2 w-full max-w-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              onClick={exportCourseCSV}
              className="ml-4 bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              Export CSV
            </button>
          </div>

          <div className="bg-white p-6 rounded shadow border">
            <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
            <div className="w-64 mx-auto">
              <Pie data={chartData} />
            </div>
          </div>

          <div className="overflow-x-auto mt-8 bg-white rounded shadow border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700">Group</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap">{row.user}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{row.group}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{row.course}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "survey" && (
        <>
          <div className="flex items-center gap-4 mb-6">
            <select
              className="border px-4 py-2 rounded w-full max-w-sm"
              value={selectedSurvey}
              onChange={(e) => setSelectedSurvey(e.target.value)}
            >
              <option value="">Select a Survey</option>
              {surveyList.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button
              onClick={exportSurveyCSV}
              disabled={!selectedSurvey}
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>

          {selectedSurvey &&
            responses[selectedSurvey].map((question, index) => {
              const labels = Object.keys(question.results);
              const data = Object.values(question.results);

              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded shadow border space-y-4 mb-8"
                >
                  <h3 className="font-semibold">{question.question}</h3>
                  <div className="w-full max-w-3xl">
                    <Bar
                      data={{
                        labels,
                        datasets: [
                          {
                            label: "Responses",
                            data,
                            backgroundColor: "#3b82f6",
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { display: false },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: { stepSize: 1 },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </>
      )}
    </div>
  );
}
